import { create } from 'zustand';
import { api } from '../services/api';

export interface SharedNote {
  id: string;
  family_id: string;
  title: string;
  content: string;
  colour: string;
  pinned: boolean;
  created_by: string;
  creator_name: string;
  last_edited_by: string | null;
  editor_name: string | null;
  created_at: string;
  updated_at: string;
}

interface NotesState {
  notes: SharedNote[];
  isLoading: boolean;
  error: string | null;
  fetchNotes: () => Promise<void>;
  createNote: (data: { title: string; content?: string; colour?: string; pinned?: boolean }) => Promise<void>;
  updateNote: (id: string, data: Partial<SharedNote>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  handleWsMessage: (message: any) => void;
}

export const useNotesStore = create<NotesState>((set, get) => ({
  notes: [],
  isLoading: false,
  error: null,

  fetchNotes: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/notes');
      set({ notes: res.data.notes, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createNote: async (data) => {
    try {
      const res = await api.post('/notes', data);
      const note = res.data.note;
      // Ensure creator_name is always present to avoid render crash
      if (!note.creator_name) {
        note.creator_name = note.editor_name || 'Me';
      }
      set(state => {
        if (state.notes.some(n => n.id === note.id)) return state;
        return { notes: [note, ...state.notes] };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateNote: async (id, data) => {
    try {
      const res = await api.put(`/notes/${id}`, data);
      set(state => ({ notes: state.notes.map(n => n.id === id ? res.data.note : n) }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteNote: async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      set(state => ({ notes: state.notes.filter(n => n.id !== id) }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    const { type, payload } = message;
    if (type === 'NOTE_CREATED') {
      set(state => {
        if (state.notes.some(n => n.id === payload.id)) return state;
        return { notes: [payload, ...state.notes] };
      });
    } else if (type === 'NOTE_UPDATED') {
      set(state => ({ notes: state.notes.map(n => n.id === payload.id ? payload : n) }));
    } else if (type === 'NOTE_DELETED') {
      set(state => ({ notes: state.notes.filter(n => n.id !== payload.noteId) }));
    }
  },
}));
